import React from 'react';
import range from 'lodash/fp/range';
import { MenuListItem } from '@atlaskit/navigation-system';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';

import { token } from '@atlaskit/tokens';
import type { SkeletonNavMenuItemsProps } from './types.tsx';

const styles = cssMap({
	flyoutWrapper: {
		maxHeight: '460px',
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
	},
	menuWrapper: {
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	projectAvatar: {
		backgroundColor: token('color.skeleton'),
		width: '24px',
		height: '24px',
		borderRadius: token('radius.small'),
	},
	flyoutMenuItem: {
		backgroundColor: token('color.skeleton'),
		height: '32px',
		width: '100%',
		borderRadius: token('radius.small'),
		marginTop: token('space.075'),
		marginBottom: token('space.075'),
	},
	menuItem: {
		backgroundColor: token('color.skeleton'),
		height: '24px',
		width: '100%',
		borderRadius: token('radius.small'),
	},
	flyoutHeading: {
		backgroundColor: token('color.skeleton'),
		height: '16px',
		width: '48px',
		borderRadius: token('radius.small'),
		marginTop: token('space.100'),
		marginBottom: token('space.150'),
	},
	menuHeading: {
		backgroundColor: token('color.skeleton'),
		height: '16px',
		width: '48px',
		borderRadius: token('radius.small'),
		marginLeft: token('space.050'),
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
	bigSearch: {
		height: '36px',
		backgroundColor: token('color.skeleton'),
		width: '100%',
		borderRadius: token('radius.small'),
		marginTop: token('space.050'),
		marginBottom: token('space.150'),
		marginRight: token('space.075'),
	},
	search: {
		backgroundColor: token('color.skeleton'),
		height: '32px',
		width: '100%',
		borderRadius: token('radius.small'),
		marginTop: token('space.050'),
		marginBottom: token('space.150'),
		marginRight: token('space.075'),
	},
	overflowingSection: {
		overflowY: 'auto',
	},
});
const Heading = () => <Box xcss={styles.menuHeading} />;
const FlyoutHeading = () => <Box xcss={styles.flyoutHeading} />;

export const ItemWithIcon = ({
	withSubTitle = false,
}: {
	withSubTitle?: boolean;
}): React.JSX.Element => (
	<Flex gap="space.050" alignItems="center" justifyContent="stretch">
		<Box xcss={styles.projectAvatar} />
		{withSubTitle ? <Box xcss={styles.flyoutMenuItem} /> : <Box xcss={styles.menuItem} />}
	</Flex>
);
const L2MenuItemSectionSkeleton = ({ withHeading, count = 5 }: SkeletonNavMenuItemsProps) => {
	return (
		<>
			{/* eslint-disable-next-line @atlassian/a11y/heading-has-content -- See https://go/a11y-heading-has-content for more details */}
			{withHeading && <Heading />}
			{range(0, count).map((index) => (
				<MenuListItem key={index}>
					<Box xcss={styles.menuWrapper}>
						<ItemWithIcon />
					</Box>
				</MenuListItem>
			))}
		</>
	);
};
const Search = ({ isBig }: { isBig?: boolean }) => (
	<Box xcss={isBig ? styles.bigSearch : styles.search} />
);

const FlyoutSkeleton = ({
	withHeading,
	count = 5,
	withSearch = false,
	withSubTitle = false,
	withSubHeading = false,
}: SkeletonNavMenuItemsProps) => {
	return (
		<Box xcss={styles.flyoutWrapper}>
			{withHeading && <FlyoutHeading />}
			{withSearch && <Search isBig={withSubTitle} />}
			{withSubHeading && <FlyoutHeading />}
			<Flex
				xcss={styles.overflowingSection}
				gap={withSubTitle ? 'space.050' : 'space.100'}
				direction="column"
				justifyContent="start"
			>
				{range(0, count).map((index) => (
					<ItemWithIcon key={index} withSubTitle={withSubTitle} />
				))}
			</Flex>
		</Box>
	);
};

const FancySkeleton = ({
	count = 5,
	withHeading,
	isFlyout,
	withSearch,
	withSubTitle,
	withSubHeading,
}: SkeletonNavMenuItemsProps) =>
	isFlyout ? (
		<FlyoutSkeleton
			withHeading={withHeading}
			count={count}
			withSearch={withSearch}
			withSubTitle={withSubTitle}
			withSubHeading={withSubHeading}
		/>
	) : (
		<L2MenuItemSectionSkeleton withHeading={withHeading} count={count} />
	);

export const SkeletonNavMenuItems = ({
	count = 5,
	withHeading,
	isFlyout,
	withSearch,
	withSubTitle,
	withSubHeading,
}: SkeletonNavMenuItemsProps): React.JSX.Element => (
	<UFOLoadHold name="nav4-sidebar-menu-items-loading">
		<FancySkeleton
			count={count}
			withHeading={withHeading}
			isFlyout={isFlyout}
			withSearch={withSearch}
			withSubTitle={withSubTitle}
			withSubHeading={withSubHeading}
		/>
	</UFOLoadHold>
);
